import moment from "moment";
import { Subtitle } from "./subtitle";
import { ConversionUtils } from "./conversion-utils";
import { RectangleF } from "./rectangle-f";
import { Tag } from "./tag";
import { MediaQCWorkflowObjective } from "./qc-workflow";

export enum ShowType {
  Media = 1,
  Live,
  Contract,
  DynamicPromo,
  Layout,
  BroadcastPreset,
  GraphicsShow,
}

export enum MediaType {
  AudioVideo,
  Video,
  Audio,
  Image,
  FlexAnimation,
  Subtitles,
  Unknown,
  Contract,
}

export enum ContentAnalysisResult {
  Ok = 0,
  DownloadFailed,
  UnknownMediaType,
  ServerFailure,
}

export enum ContentUploadStatus {
  Enabled = 0,
  Disabled,
  Pending,
}

export enum MediaLinkedType {
  Trailer = 1,
  Audio = 2,
  Contract = 3,
}

export enum FrameFormatEnum {
  Unknown = 0,
  Progressive,
  TopFieldFirst,
  BottomFieldFirst,
}

export class CropSettings {
  public static createNoCropSettings = (convertToOneDeciaml: boolean = true) =>
    new CropSettings(0, 0, 0, 0, convertToOneDeciaml);

  constructor(
    public top: number,
    public bottom: number,
    public left: number,
    public right: number,
    private convertToOneDeciaml = true,
  ) {
    if (convertToOneDeciaml) {
      this.top = this.toOneDecimal(top);
      this.left = this.toOneDecimal(left);
      this.right = this.toOneDecimal(right);
      this.bottom = this.toOneDecimal(bottom);
    } else {
      this.top = top;
      this.left = left;
      this.right = right;
      this.bottom = bottom;
    }
  }

  public isNoCrop(): boolean {
    return this.equals(CropSettings.createNoCropSettings());
  }

  public reset(): void {
    this.top = 0;
    this.left = 0;
    this.right = 0;
    this.bottom = 0;
  }

  public toCropSettingsRequest() {
    if (this.top + this.bottom > 100 || this.left + this.right > 100) {
      throw new Error("Sizing problem");
    }
    return {
      Top: this.top / 100,
      Left: this.left / 100,
      Right: this.right / 100,
      Bottom: this.bottom / 100,
    };
  }

  public equals(to: CropSettings) {
    if (to) {
      return (
        to.top === this.top &&
        to.bottom === this.bottom &&
        to.left === this.left &&
        to.right === this.right
      );
    }
    return false;
  }

  private toOneDecimal(num: number) {
    return Math.round(num * 10) / 10;
  }
}

export class MediaItem {
  public contentMediaDurationMs: number;

  /** Upload/creation time in ms (from list API); used e.g. for "Recently Added" sort. */
  public uploadedTimeMs?: number;

  constructor(
    public id?: number,
    public uid?: string,
    public name?: string,
    public userFilename?: string,
    public contentMediaType?: MediaType,
    public contentDuration?: moment.Duration,
    public contentSnapshotUrl?: string,
    public contentFps?: number,
    public contentUploadStatus?: ContentUploadStatus,
    public parentMediaId?: number,
    public mediaLinkedType?: MediaLinkedType,
    public tenantMediaId?: string,
    public quality?: number,
    public recording?: boolean,
  ) {
    this.contentMediaDurationMs = contentDuration
      ? contentDuration.asMilliseconds()
      : 0;
  }

  formatDuration(fpsRate?: number) {
    if (!fpsRate) {
      return moment
        .utc(this.contentDuration.asMilliseconds())
        .format("HH:mm:ss.SSS");
    }

    const fps = ConversionUtils.getFpsFromMs(
      this.contentDuration.milliseconds(),
      fpsRate,
    );
    return (
      moment
        .utc(
          this.contentDuration.asMilliseconds() -
            this.contentDuration.milliseconds(),
        )
        .format("HH:mm:ss.") + (fps < 10 ? "0" + fps : "" + fps)
    );
  }
  public formatMomemtDuration(
    duration: moment.Duration,
    fpsRate?: number,
  ): string {
    if (!fpsRate) {
      return moment.utc(duration.asMilliseconds()).format("HH:mm:ss.SSS");
    }

    const fps = ConversionUtils.getFpsFromMs(duration.milliseconds(), fpsRate);
    return (
      moment
        .utc(duration.asMilliseconds() - duration.milliseconds())
        .format("HH:mm:ss.") + (fps < 10 ? "0" + fps : "" + fps)
    );
  }
}
export class MediaSegment {
  constructor(
    public parentMedia: MediaInfo,
    public tenantUniqueId: number,
    public uid: string,
    public description: string,
    public timeInMs: number,
    public timeOutMs: number,
  ) {}
  public get duration(): number {
    return this.timeOutMs - this.timeInMs;
  }
  public set duration(newDuration: number) {
    this.timeOutMs = newDuration + this.timeInMs;
  }
}

export class UpsertSegmnetEntry {
  constructor(
    public tenantMediaUniqueId: string,
    public description: string,
    public tcIn: string,
    public tcOut: string,
  ) {}
}
export class UpsertSegmnetsResultEntry {
  constructor(
    public status: EUpsertSegmnetsResultStatus,
    public message: string,
    public requestIndex?: number,
  ) {}
}

export enum EUpsertSegmnetsResultStatus {
  OK,
  Error,
}
export class MediaInfo extends MediaItem {
  constructor(
    public id: number,
    public uid: string,
    public name: string,
    public userFilename: string,
    public contentMediaType: MediaType,
    public contentDuration: moment.Duration,
    public contentSnapshotUrl: string,
    public contentUploadStatus: ContentUploadStatus,
    public parentMediaId: number,
    public mediaLinkedType: MediaLinkedType,
    public contentFps: number,
    public dateCreated: moment.Moment,
    public contentHeight: number,
    public contentWidth: number,
    public description: string,
    public licenseFrom: moment.Moment,
    public licenseTo: moment.Moment,
    public contentHasAlpha: boolean,
    public contentAnalysisResult: ContentAnalysisResult,
    public uploaderUserId: number,
    public uploadTime: moment.Moment,
    public analyzedTime: moment.Moment,
    public isUploadedToIChannelStorage: boolean,
    public tags: Tag[],
    public assetTypes: Tag[],
    public dayParting: Tag[],
    public mediaLicenseCountryIds: number[],
    public tenantMediaId: string,
    public subtitles: Subtitle[],
    public audioFiles: {
      id: number;
      name: string;
      language: string;
      contentUploadStatus: ContentUploadStatus;
    }[],
    public packshotIsAnalyzed: boolean,
    public trailers: {
      id: number;
      thumbnailUrl: string;
      contentUploadStatus: ContentUploadStatus;
      name: string;
    }[],
    public metadata: { id: number; value: string }[],
    public frameFormat: FrameFormatEnum,
    public OriginalMediaIsDeleted: boolean,
    public sampleRate: number,
    public channelCount: number,
    public generalVolume: number,
    public transcodedUrl: string,
    public duringRecording: boolean,
    public contentUrl: string,
    public cropSettings: CropSettings,
    private _pixelAspectHeight: number,
    private _pixelAspectWidth: number,
    public startTimeStampMs: number,
    public segments: MediaSegment[] = [],

    public floatingMediaDepth: number,
    public transcodedAlternateUrls: string[],
    public mediaTypeValue?: number,
    public recording?: boolean,
    public floatingMediaPosition?: RectangleF,
    public lufs?: number,
    public mediaQCWorkflowObjectives?: MediaQCWorkflowObjective[],
    public mediaShouldSqueeze?: boolean,
    public closedCaptions: Subtitle[] = [],
  ) {
    super(
      id,
      uid,
      name,
      userFilename,
      contentMediaType,
      contentDuration,
      contentSnapshotUrl,
      contentFps,
      contentUploadStatus,
      parentMediaId,
      mediaLinkedType,
      tenantMediaId,
    );
  }

  get pixelAspectWidth() {
    return this._pixelAspectWidth ? this._pixelAspectWidth : 1;
  }
  formatStartTime(fpsRate?: number) {
    const durationMoment = moment.duration(
      this.startTimeStampMs,
      "milliseconds",
    );
    return this.formatMomemtDuration(durationMoment, fpsRate);
  }
  get pixelAspectHeight() {
    return this._pixelAspectHeight ? this._pixelAspectHeight : 1;
  }

  get formattedLufs(): string {
    if (this.lufs) {
      return ((Math.log(this.lufs) / Math.log(2)) * 6).toString();
    } else {
      return "Ø";
    }
  }
  public gcd() {
    //console.log('gcd ', this.pixelAspectWidth);
    //console.log('gcd ', this.pixelAspectHeight);
    let n = this.contentWidth * this.pixelAspectWidth;
    let m = this.contentHeight * this.pixelAspectHeight;
    let r = 0;
    while (n !== 0) {
      r = m % n;
      m = n;
      n = r;
    }
    return m;
  }

  public getAspectRation() {
    /*const gcd = this.gcd();
      return (this.contentWidth / gcd) / (this.contentHeight / gcd);*/
    return (
      (this.contentWidth * this.pixelAspectWidth) /
      (this.contentHeight * this.pixelAspectHeight)
    );
  }
}
